import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth as authApi } from '../lib/api.js';
import { setAuth } from '../lib/auth.js';
import { useBranding } from '../components/LogoMais.jsx';

export default function LoginPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const b = useBranding();

  const [username, setUsername] = useState('');
  const [senha, setSenha]       = useState('');
  const [erro, setErro]         = useState(null);
  const [carregando, setLoad]   = useState(false);

  // Branding com fallback (caso /v1/branding ainda esteja carregando)
  const corPrimaria   = b?.cor_primaria   || '#F15D22';
  const corSecundaria = b?.cor_secundaria || '#FEBA12';
  const corDestaque   = b?.cor_destaque   || '#673091';
  const nomeEmpresa   = b?.nome_empresa   || 'Mais Internet';
  const tagline       = b?.tagline        || 'Field Service';
  const logoUrl       = b?.logo_url;

  async function entrar(e) {
    e.preventDefault();
    setErro(null);
    setLoad(true);
    try {
      const r = await authApi.login(username, senha);
      setAuth(r.data.token, r.data.operador);
      nav(next, { replace: true });
    } catch (e) {
      setErro(e.uiMessage || 'Falha no login');
      setLoad(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      // Gradiente com as cores da marca (laranja → amarelo)
      // Diagonal pra dar movimento + roxo sutil no canto
      background: `linear-gradient(135deg, ${corPrimaria} 0%, ${corSecundaria} 100%)`,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Detalhe roxo decorativo no canto superior direito */}
      <div style={{
        position: 'absolute', top: '-15%', right: '-10%',
        width: 480, height: 480, borderRadius: '50%',
        background: `radial-gradient(circle, ${corDestaque}33 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      {/* Detalhe roxo decorativo no canto inferior esquerdo */}
      <div style={{
        position: 'absolute', bottom: '-15%', left: '-10%',
        width: 540, height: 540, borderRadius: '50%',
        background: `radial-gradient(circle, ${corDestaque}22 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <form onSubmit={entrar} style={{
        background: '#fff',
        borderRadius: 16,
        padding: '2.5rem 2rem 2rem',
        width: '100%',
        maxWidth: 400,
        boxShadow: '0 20px 50px rgba(0,0,0,0.25), 0 8px 16px rgba(0,0,0,0.08)',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Logo + título */}
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={nomeEmpresa}
              style={{
                maxWidth: 180,
                maxHeight: 70,
                objectFit: 'contain',
                marginBottom: '1rem',
              }}
            />
          ) : (
            <h2 style={{
              margin: '0 0 0.5rem',
              fontSize: '1.4rem',
              color: corPrimaria,
              fontWeight: 800,
              letterSpacing: '-0.5px',
            }}>
              {nomeEmpresa}
            </h2>
          )}

          {/* Tagline destacada */}
          <div style={{
            display: 'inline-block',
            padding: '4px 14px',
            borderRadius: 99,
            background: `linear-gradient(90deg, ${corPrimaria}15, ${corSecundaria}15)`,
            border: `1px solid ${corPrimaria}33`,
            fontSize: '0.8rem',
            fontWeight: 700,
            color: corPrimaria,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}>
            {tagline}
          </div>
        </div>

        <div className="stack">
          <div>
            <label style={{ fontWeight: 600, color: '#374151' }}>Usuário</label>
            <input
              type="text"
              value={username}
              autoFocus
              autoComplete="username"
              onChange={(e) => setUsername(e.target.value)}
              disabled={carregando}
              style={{
                borderColor: username ? corPrimaria + '66' : undefined,
                transition: 'border-color 0.15s',
              }}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, color: '#374151' }}>Senha</label>
            <input
              type="password"
              value={senha}
              autoComplete="current-password"
              onChange={(e) => setSenha(e.target.value)}
              disabled={carregando}
              style={{
                borderColor: senha ? corPrimaria + '66' : undefined,
                transition: 'border-color 0.15s',
              }}
            />
          </div>

          {erro && (
            <div style={{
              background: '#fee2e2',
              color: '#991b1b',
              padding: '0.625rem 0.75rem',
              borderRadius: 8,
              fontSize: '0.85rem',
              border: '1px solid #fecaca',
            }}>
              ⚠ {erro}
            </div>
          )}

          <button
            type="submit"
            disabled={carregando || !username || !senha}
            style={{
              marginTop: '0.5rem',
              padding: '0.75rem',
              borderRadius: 8,
              border: 'none',
              background: carregando || !username || !senha
                ? '#9ca3af'
                : `linear-gradient(90deg, ${corPrimaria}, ${corSecundaria})`,
              color: '#fff',
              fontSize: '0.95rem',
              fontWeight: 700,
              letterSpacing: '0.3px',
              cursor: carregando || !username || !senha ? 'not-allowed' : 'pointer',
              boxShadow: carregando || !username || !senha
                ? 'none'
                : `0 4px 12px ${corPrimaria}55`,
              transition: 'transform 0.1s, box-shadow 0.15s',
            }}
            onMouseDown={e => !carregando && username && senha && (e.currentTarget.style.transform = 'translateY(1px)')}
            onMouseUp={e => (e.currentTarget.style.transform = 'translateY(0)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            {carregando ? 'Entrando...' : 'Entrar'}
          </button>
        </div>

        <p style={{
          textAlign: 'center',
          marginTop: '1.75rem',
          color: '#9ca3af',
          fontSize: '0.72rem',
          letterSpacing: '0.2px',
        }}>
          Acesso restrito · Em caso de problemas, contate o admin
        </p>
      </form>
    </div>
  );
}
